if (!customElements.get('subscription-picker')) {
  customElements.define(
    'subscription-picker',
    class SubscriptionPicker extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.purchaseOptionInputs = this.querySelectorAll('input[name="purchase_option"]');
        this.productForm = document.getElementById(this.dataset.productFormId);
        this.variantIdInput = this.productForm?.querySelector('input[name="id"]');
        this.sellingPlanInput = this.productForm?.querySelector('input.product-selling-plan-id[name="selling_plan"]');

        const dataScript = this.querySelector('#selling-plans-data');
        if (dataScript) {
          this.productData = JSON.parse(dataScript.textContent);
        }

        this.syncSellingPlanInput();
        this.addEventListeners();
      }

      addEventListeners() {
        this.purchaseOptionInputs.forEach((input) => {
          input.addEventListener('change', this.handleSellingPlanChange.bind(this));
        });

        if (this.variantIdInput) {
          this.variantIdInput.addEventListener('change', this.handleVariantChange.bind(this));
        }

        document.addEventListener('variant:change', this.handleVariantChange.bind(this));
      }

      handleSellingPlanChange(event) {
        const selectedSellingPlan = event.target.value;
        this.syncSellingPlanInput();
        this.updatePricing();
        this.dispatchSubscriptionChangeEvent(selectedSellingPlan);
      }

      handleVariantChange(event) {
        const variant = event.detail ? event.detail.variant : this.getCurrentVariant();
        if (variant) {
          this.updateSubscriptionPricing(variant);
        }
      }

      /**
       * Accelerated checkout only reliably reads name="selling_plan" from an
       * input physically inside the product form (not form-associated radios).
       * One-time: disable the input so no plan is sent. Subscription: enable + set id.
       */
      syncSellingPlanInput() {
        if (!this.sellingPlanInput) return;

        const selectedInput = this.querySelector('input[name="purchase_option"]:checked');
        const sellingPlanId = selectedInput?.dataset.sellingPlanId || selectedInput?.value || '';

        if (sellingPlanId) {
          this.sellingPlanInput.disabled = false;
          this.sellingPlanInput.value = sellingPlanId;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }

      updatePricing() {
        const selectedInput = this.querySelector('input[name="purchase_option"]:checked');
        if (!selectedInput) return;

        const variant = this.getCurrentVariant();
        if (!variant) return;

        this.updateSubscriptionPricing(variant);
      }

      updateSubscriptionPricing(variant) {
        this.purchaseOptionInputs.forEach((input) => {
          const label = input.nextElementSibling;
          if (!label) return;

          const priceElement = label.querySelector('.subscription-picker__option-price .price');
          const comparePriceElement = label.querySelector('.subscription-picker__option-price .price--compare');
          const savingsElement = label.querySelector('.subscription-picker__savings');
          if (!priceElement) return;

          if (input.value === '') {
            priceElement.textContent = this.formatMoney(variant.price);
          } else {
            const sellingPlan = this.getSellingPlan(input.value);
            if (sellingPlan && sellingPlan.price_adjustments && sellingPlan.price_adjustments.length > 0) {
              const priceAdjustment = sellingPlan.price_adjustments[0];
              let subscriptionPrice = variant.price;

              if (priceAdjustment.value_type === 'percentage') {
                subscriptionPrice = Math.round((variant.price * (100 - priceAdjustment.value)) / 100);
              } else {
                subscriptionPrice = variant.price - priceAdjustment.value;
              }

              priceElement.textContent = this.formatMoney(subscriptionPrice);

              if (comparePriceElement) {
                if (subscriptionPrice < variant.price) {
                  comparePriceElement.textContent = this.formatMoney(variant.price);
                  comparePriceElement.style.display = '';
                } else {
                  comparePriceElement.style.display = 'none';
                }
              }

              if (savingsElement && subscriptionPrice < variant.price) {
                const savingsAmount = variant.price - subscriptionPrice;
                const savingsPercentage = Math.round((savingsAmount * 100) / variant.price);
                savingsElement.textContent = `Save ${savingsPercentage}% (${this.formatMoney(savingsAmount)})`;
              }

              this.updateTotalSavings(variant.price, subscriptionPrice);
            }
          }
        });
      }

      updateTotalSavings(variantPrice, subscriptionPrice) {
        const totalSavingsEl = this.querySelector('[data-total-savings-benefits]');
        if (!totalSavingsEl) return;

        const flatFeeCents = parseInt(this.dataset.benefitsFlatFeeCents || 0);
        const planDiscountCents = subscriptionPrice < variantPrice ? variantPrice - subscriptionPrice : 0;
        const totalSavings = planDiscountCents + flatFeeCents;

        const amountEl = totalSavingsEl.querySelector('.subscription-picker__total-savings-benefits-amount');
        if (amountEl) amountEl.textContent = this.formatMoney(totalSavings);
        totalSavingsEl.hidden = totalSavings === 0;
      }

      getCurrentVariant() {
        if (!this.variantIdInput) return null;

        const variantId = parseInt(this.variantIdInput.value);
        return this.productData?.variants?.find((v) => v.id === variantId) || null;
      }

      getSellingPlan(sellingPlanId) {
        if (!this.productData?.sellingPlans) return null;
        return this.productData.sellingPlans.find((plan) => plan.id == sellingPlanId);
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      dispatchSubscriptionChangeEvent(sellingPlanId) {
        this.dispatchEvent(
          new CustomEvent('subscription:change', {
            detail: { sellingPlanId },
            bubbles: true,
          })
        );
      }
    }
  );
}
